/**
 * One catalog card.
 *
 * Everything shown here comes from `GET /integrations` rather than a hardcoded
 * list, and three distinctions the handoff insists on are visible on the card
 * itself:
 *
 *   • **Unavailable is administrator setup, not a user failure.** Such a card
 *     explains that and offers no working Connect action.
 *   • **Native and Composio connections are separate systems.** A user can hold
 *     both for the same app, so both statuses are shown, and a
 *     `supportsNative` app is never assumed to have a native connection.
 *   • **Zendesk is two products.** The native connection is Sell; this catalog
 *     app is Support.
 *
 * WordPress.com gets content controls only — it has no contact import — so its
 * card never offers one.
 */

import { AlertTriangle, Check, Link2, Loader2, Lock, Plug, RefreshCw } from "lucide-react";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { cn } from "lib/utils";
import type { CatalogApp, Capability, ConnectionStatus } from "core/features/integrations";
import {
    isKnownConnectionStatus,
    operationsFor,
    supportsContactImport,
} from "core/features/integrations";

/** DOM id of an app's card, so focus can be returned to it after a redirect. */
export const appCardId = (appId: string): string => `integration-card-${appId}`;

const CAPABILITY_LABEL: Record<Capability, string> = {
    contact_import: "Contact import",
    scheduled_sync: "Scheduled sync",
    content_connection: "Content",
};

const STATUS: Record<ConnectionStatus, { label: string; className: string }> = {
    active: {
        label: "Connected",
        className: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    },
    disconnected: {
        label: "Not connected",
        className: "bg-surface-muted text-content-muted",
    },
    paused_auth: {
        label: "Needs reconnect",
        className: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    },
    disconnecting: {
        label: "Disconnecting…",
        className: "bg-surface-muted text-content-muted",
    },
};

export interface AppCardProps {
    app: CatalogApp;
    connecting?: boolean;
    disconnecting?: boolean;
    onConnect: (app: CatalogApp) => void;
    onDisconnect: (app: CatalogApp) => void;
    onManage: (app: CatalogApp) => void;
}

export function AppCard({
    app,
    connecting,
    disconnecting,
    onConnect,
    onDisconnect,
    onManage,
}: AppCardProps) {
    /* An unrecognised status is a refresh state — never quietly drawn as "not
       connected", which would offer a Connect button for something that may
       well be connected. */
    const known = isKnownConnectionStatus(app.connectionStatus);
    const status = known
        ? STATUS[app.connectionStatus]
        : {
              label: "Unknown state",
              className: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
          };
    const isActive = app.connectionStatus === "active";
    const isPausedAuth = app.connectionStatus === "paused_auth";
    /* `disconnecting` means remote cleanup has not finished. The handoff says
       not to suggest an immediate reconnect until it does. */
    const isDisconnecting = app.connectionStatus === "disconnecting" || disconnecting;
    const canImport = supportsContactImport(app.id) && app.capabilities.includes("contact_import");
    /* WordPress.com has no contact import but does have content controls, so
       the way into its panel cannot depend on import alone. */
    const hasContent = operationsFor(app.id).length > 0;

    return (
        <div
            id={appCardId(app.id)}
            tabIndex={-1}
            className="flex flex-col rounded-xl border border-line bg-surface p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
            <div className="flex items-start gap-3">
                {app.logoUrl ? (
                    <img
                        src={app.logoUrl}
                        alt=""
                        width={36}
                        height={36}
                        className="h-9 w-9 shrink-0 rounded-lg object-contain"
                    />
                ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-muted">
                        <Plug className="h-4 w-4 text-content-muted" aria-hidden="true" />
                    </span>
                )}

                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="truncate text-ui font-semibold text-content">{app.name}</h3>
                        {/* Zendesk: this app is Support; the native one is Sell. */}
                        {app.id === "zendesk" && (
                            <Badge variant="secondary" className="shrink-0">
                                Support
                            </Badge>
                        )}
                    </div>
                    <p className="mt-0.5 truncate text-meta text-content-muted">{app.category}</p>
                </div>

                <span
                    className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-meta font-medium",
                        status.className,
                    )}
                >
                    {isDisconnecting ? "Disconnecting…" : status.label}
                </span>
            </div>

            {/* The native connection is a separate system — never overwritten,
                never migrated, and shown on its own line so it cannot be read
                as the Composio one. */}
            {app.nativeConnectionStatus && (
                <p className="mt-3 flex items-center gap-1.5 rounded-lg bg-surface-muted px-2 py-1.5 text-meta text-content-muted">
                    <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span>
                        Native{app.nativeProduct ? ` ${app.nativeProduct}` : ""} connection:{" "}
                        <span className="font-medium text-content">{app.nativeConnectionStatus}</span>
                    </span>
                </p>
            )}

            {app.capabilities.length > 0 && (
                <ul className="mt-3 flex flex-wrap gap-1.5">
                    {app.capabilities.map((capability) => (
                        <li
                            key={capability}
                            className="rounded-md bg-surface-muted px-1.5 py-0.5 text-meta text-content-muted"
                        >
                            {CAPABILITY_LABEL[capability] ?? capability}
                        </li>
                    ))}
                </ul>
            )}

            <p className="mt-3 text-meta text-content-muted">{app.authentication}</p>

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-3">
                {!known ? (
                    <p className="flex items-center gap-1.5 text-meta text-amber-700 dark:text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        This app is in a state this page does not recognise. Refresh to check.
                    </p>
                ) : !app.available ? (
                    /* Administrator setup state. No working Connect action. */
                    <p className="flex items-center gap-1.5 text-meta text-content-muted">
                        <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        Administrator setup required
                    </p>
                ) : isDisconnecting ? (
                    <p className="flex items-center gap-1.5 text-meta text-content-muted">
                        <Loader2
                            className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none"
                            aria-hidden="true"
                        />
                        Finishing disconnection — reconnect once this clears.
                    </p>
                ) : (
                    <>
                        {isActive && (
                            <>
                                {(canImport || hasContent) && (
                                    <Button size="sm" variant="secondary" onClick={() => onManage(app)}>
                                        {canImport ? "Set up import" : "Content"}
                                    </Button>
                                )}
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => onDisconnect(app)}
                                >
                                    Disconnect
                                </Button>
                                <span className="ml-auto flex items-center gap-1 text-meta text-emerald-700 dark:text-emerald-300">
                                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                                    Active
                                </span>
                            </>
                        )}

                        {isPausedAuth && (
                            <>
                                <Button
                                    size="sm"
                                    variant="primary"
                                    loading={connecting}
                                    onClick={() => onConnect(app)}
                                >
                                    <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                                    Reconnect
                                </Button>
                                <p className="flex items-center gap-1.5 text-meta text-amber-700 dark:text-amber-300">
                                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                    Import profiles may need saving again after reconnecting.
                                </p>
                            </>
                        )}

                        {app.connectionStatus === "disconnected" && (
                            <Button
                                size="sm"
                                variant="primary"
                                loading={connecting}
                                onClick={() => onConnect(app)}
                            >
                                Connect
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* A supportsNative app may have no native connection at all, so this
                says what is possible rather than what exists. */}
            {app.supportsNative && !app.nativeConnectionStatus && (
                <p className="mt-2 text-meta text-content-muted">
                    A separate native connection is also available for this app.
                </p>
            )}
        </div>
    );
}
